import React, { useEffect, useMemo, useState } from "react";
import { FaTimes, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { getHotelFolder } from "../../assets/hotelImages";
import manifest from "../../assets/hotelImagesManifest.json";
import "./HotelGallery.css";

const categoryLabels = {
  "hotel-outside-view": "Hotel Views",
  "hotel-daytime-view": "Hotel Views",
  "hotel-exterior": "Hotel Views",
  "hotel-lobby": "Lobby & Reception",
  "lounge-bar-restaurant": "Restaurant & Bar",
  "bar": "Restaurant & Bar",
  "restaurant": "Restaurant & Bar",
  "outdoor-pool": "Pool & Recreation",
  "swimming-pool": "Pool & Recreation",
  "pool": "Pool & Recreation",
  "gym": "Gym & Fitness",
  "fitness": "Gym & Fitness",
};

const roomCategoryWords = ["room", "suite", "deluxe", "plus"];

const HOTEL_PAUSE = 4500;
const STEP_PAUSE = 2800;

const HotelGallery = ({ hotelName, hideHead = false }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [step, setStep] = useState(0);
  const [jump, setJump] = useState(false);

  const groups = useMemo(() => {
    const folder = getHotelFolder(hotelName);
    if (!folder) return [];
    const categories = (manifest[folder] && manifest[folder].categories) || {};
    return Object.keys(categories)
      .filter((cat) => !roomCategoryWords.some((w) => cat.includes(w)))
      .map((cat) => ({
        label: categoryLabels[cat] || "More Amenities",
        images: categories[cat],
      }))
      .filter((g) => g.images && g.images.length > 0);
  }, [hotelName]);

  const items = groups.reduce(
    (acc, g) => acc.concat(g.images.map((src) => ({ src, label: g.label }))),
    []
  );

  const total = items.length;
  const shift = total > 0 ? step * (100 / (2 * total)) : 0;

  useEffect(() => {
    if (total === 0) return;
    const pause = (step + 1) % 4 === 0 ? HOTEL_PAUSE : STEP_PAUSE;
    const id = setTimeout(() => {
      setStep((s) => {
        if (s >= total - 1) {
          setJump(true);
          requestAnimationFrame(() =>
            requestAnimationFrame(() => setJump(false))
          );
          return 0;
        }
        return s + 1;
      });
    }, pause);
    return () => clearTimeout(id);
  }, [step, total]);

  if (total === 0) return null;

  const close = () => setActiveIndex(null);
  const prev = () =>
    setActiveIndex((i) => (i - 1 + total) % total);
  const next = () => setActiveIndex((i) => (i + 1) % total);

  return (
    <section className='rl-gallery'>
      {!hideHead && (
        <div className='rl-section-head'>
          <div className='rl-section-head__eyebrow'>Explore the hotel</div>
          <h2>Gallery & Amenities</h2>
        </div>
      )}
      <div className='rg-marquee'>
        <div
          className={`rg-track${jump ? " rg-track--jump" : ""}`}
          style={{ transform: `translateX(-${shift}%)` }}
        >
          {[...items, ...items].map((item, i) => (
            <button
              key={i}
              type='button'
              className='rg-slide'
              onClick={() => setActiveIndex(i % total)}
            >
              <img src={item.src} alt={item.label} />
              <span className='rg-slide__tag'>{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      {activeIndex !== null && (
        <div className='rg-lightbox' onClick={close}>
          <div
            className='rg-lightbox__card'
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type='button'
              className='rg-lightbox__close'
              onClick={close}
              aria-label='Close'
            >
              <FaTimes />
            </button>
            <img src={items[activeIndex].src} alt='Hotel amenity' />
            <div className='rg-lightbox__foot'>
              <button type='button' onClick={prev} aria-label='Previous'>
                <FaChevronLeft />
              </button>
              <span>
                {activeIndex + 1} / {total}
              </span>
              <button type='button' onClick={next} aria-label='Next'>
                <FaChevronRight />
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default HotelGallery;